/**
 * MilestoneFormModal, Add / Edit modal for the Milestones tool.
 *
 * Reuses the workspace's existing modal classes (`.bp-modal-overlay`,
 * `.bp-modal-sheet`, etc) so it matches the visual language of the
 * Expense Form Modal, no new CSS surface to maintain.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar as CalendarIcon, Check as CheckIcon } from 'lucide-react';

const MilestoneFormModal = ({ open, onClose, onSubmit, initial = null }) => {
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initial?.milestone_name || '');
    setStartDate(initial?.milestone_start_date?.slice(0, 10) || '');
    setEndDate(initial?.milestone_end_date?.slice(0, 10) || '');
    setNotes(initial?.notes || '');
  }, [open, initial]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return;
    setSaving(true);
    await onSubmit?.({ name: name.trim(), startDate, endDate, notes });
    setSaving(false);
    onClose?.();
  };

  const node = (
    <div className="bp-workspace">
      <div
        className="bp-modal-overlay"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        data-testid="milestone-form-modal"
      >
        <form
          className="bp-modal-sheet"
          onClick={(e) => e.stopPropagation()}
          onSubmit={submit}
          noValidate
          style={{ maxWidth: 520 }}
        >
          <div className="bp-modal-grabber" aria-hidden="true" />
          <button
            type="button"
            className="bp-modal-close"
            onClick={onClose}
            aria-label="Close"
            data-testid="milestone-form-cancel"
            style={{ position: 'absolute', top: 14, right: 14, background: 'transparent', border: 0, cursor: 'pointer', color: '#64748b' }}
          >
            <X size={18} />
          </button>
          <h2 className="bp-modal-title">
            {initial ? 'Edit milestone' : 'Add milestone'}
          </h2>
          <p className="bp-modal-sub">
            {initial
              ? 'Update the details below. Saves sync to your phone instantly.'
              : 'Track a key date for your build. Start + target dates are optional.'}
          </p>
          <div className="bp-modal-body">
            <div className="bp-form-row">
              <label htmlFor="ms-name" className="bp-form-label">Milestone name *</label>
              <input
                id="ms-name"
                type="text"
                className="bp-form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Slab pour"
                required
                autoFocus
                data-testid="milestone-form-name"
              />
            </div>
            <div className="bp-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label htmlFor="ms-start" className="bp-form-label">
                  <CalendarIcon size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Start date
                </label>
                <input
                  id="ms-start"
                  type="date"
                  className="bp-form-input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="milestone-form-start"
                />
              </div>
              <div>
                <label htmlFor="ms-end" className="bp-form-label">
                  <CalendarIcon size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Target / end date
                </label>
                <input
                  id="ms-end"
                  type="date"
                  className="bp-form-input"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="milestone-form-end"
                />
              </div>
            </div>
            <div className="bp-form-row">
              <label htmlFor="ms-notes" className="bp-form-label">Notes</label>
              <textarea
                id="ms-notes"
                className="bp-form-input"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything to remember, variations, who, why"
                data-testid="milestone-form-notes"
              />
            </div>
          </div>
          <div className="bp-modal-actions">
            <button
              type="button"
              className="bp-btn-pill bp-btn-ghost"
              onClick={onClose}
              data-testid="milestone-form-cancel-btn"
            >Cancel</button>
            <button
              type="submit"
              className="bp-btn-pill bp-btn-primary"
              disabled={!name.trim() || saving}
              data-testid="milestone-form-submit"
            >
              <CheckIcon size={14} style={{ marginRight: 6 }} />
              {saving ? 'Saving…' : (initial ? 'Save changes' : 'Add milestone')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
};

export default MilestoneFormModal;
